import * as Yup from 'yup';
import React from "react";
import {TranslatedText, getComuniServiti, getValidationRule} from "../../../helpers/helpers";
import {
  CaseForms,
  CaseMutationType,
  AttachmentType,
  AnalisiAcquaType,
  CaseStepperType,
  PaymentType,
  AnalisiAcquaPrices
} from "../../../helpers/enums";
import {getEvictionForMutation, SEND_SIMPLE_CASE} from "../../../helpers/mutations";
import PrivacyPolicy from "../../common/PrivacyPolicy/PrivacyPolicy";

// Il model definisce la struttura dei dati
// formId è un identificatore del form
// formField contiene la definizione dei singoli campi
const model = {
  formId: CaseForms.AnalisiAcqua,
  // step 1
  formField: {
    businessName: {
      name: 'businessName',
      label: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'input',
    },
    integraClientCode: {
      name: 'integraClientCode',
      label: <TranslatedText i18nKey="cases.request_info.fields.codice_cliente.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.codice_cliente.placeholder"/>,
      type: 'input',
    },
    landlinePhoneNumber: {
      name: 'landlinePhoneNumber',
      label: <TranslatedText i18nKey="cases.request_info.fields.telefono.label.required"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.telefono.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.telefono.required'/>,
      invalidErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.telefono.invalid'/>,
      type: 'input',
    },
    mobilePhoneNumber: {
      name: 'mobilePhoneNumber',
      label: <TranslatedText i18nKey="cases.request_info.fields.cellulare.label.required"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.cellulare.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.cellulare.required'/>,
      invalidErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.cellulare.invalid'/>,
      type: 'input',
    },
    email: {
      name: 'email',
      label: <TranslatedText i18nKey="cases.request_info.fields.email.label.required"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.email.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.email.required'/>,
      invalidErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.email.invalid'/>,
      type: 'input',
    },
    pec: {
      name: 'pec',
      label: <TranslatedText i18nKey="cases.request_info.fields.pec.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.pec.placeholder"/>,
      invalidErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.pec.invalid'/>,
      type: 'input',
    },

    // step 2
    nominativoAnalisi: {
      name: 'nominativoAnalisi',
      label: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'input',
    },
    comuneAnalisi: {
      name: 'comuneAnalisi',
      label: <TranslatedText i18nKey="cases.request_info.fields.comune.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.comune.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'suggestions',
      message: true,
      getItems: async (setData) => {
        try {
          const items = await getComuniServiti();
          return setData(items);
        } catch (error) {
          console.log('Error fetching comuni serviti: ', error);
          setData([]);
        }
      },
      getSuggestions: (data) => {
        return data;
      },
      filter: (value, data) => {
        return data.filter(item => {
          return String(item.comune).toLowerCase().indexOf(String(value).toLowerCase()) > -1;
        })
      },
      onSelected: (value, data, setFieldValue) => {
        const [selected] = data.filter(item => String(item.comune).toLowerCase() === String(value).toLowerCase());
        if (selected) {
          setFieldValue('cap', selected.cap);
        }
      },
      onBlur: (value, data, setValue) => {
        const [selected] = data.filter(item => String(item.comune).toLowerCase() === String(value).toLowerCase());
        selected ? setValue(selected.comune) : setValue('');
      }
    },
    indirizzo: {
      name: 'indirizzo',
      label: <TranslatedText i18nKey="cases.request_info.fields.indirizzo.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.indirizzo.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'input',
    },
    cap: {
      name: 'cap',
      label: <TranslatedText i18nKey="cases.request_info.fields.cap.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.cap.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'input',
      readonly: true
    },


    // step 3
    nominativoFatturazione: {
      name: 'nominativoFatturazione',
      label: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.error_message"/>,
      type: 'input'
    },
    codiceFiscale: {
      name: 'codiceFiscale',
      label: <TranslatedText i18nKey="cases.request_info.fields.codiceFiscale.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.codiceFiscale.placeholder"/>,
      invalidErrorMsg: <TranslatedText i18nKey="cases.request_info.fields.codiceFiscale.invalid"/>,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.fields.codiceFiscale.error_message"/>,
      uppercase: true,
      type: 'input'
    },
    partitaIvo: {
      name: 'partitaIvo',
      label: <TranslatedText i18nKey="cases.request_info.fields.partitaIvo.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.partitaIvo.placeholder"/>,
      invalidErrorMsg: <TranslatedText i18nKey="cases.request_info.fields.partitaIvo.invalid"/>,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.fields.partitaIvo.error_message"/>,
      type: 'input'
    },
    codiceFatturazione: {
      name: 'codiceFatturazione',
      label: <TranslatedText i18nKey="cases.request_info.fields.codiceFatturazione.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.codiceFatturazione.placeholder"/>,
      // requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'input'
    },
    // Step 4 - Indirizzo di fatturazione
    indirizzoFatturazione: {
      name: 'indirizzoFatturazione',
      label: <TranslatedText i18nKey="cases.request_info.fields.indirizzo.label"/>,
      placeholder: <TranslatedText i18nKey="cases.allacciamento.step4_1.datiImmobile_indirizzoCivico.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'google-address',
      suggestionType: 'address',
      onSelect: (components, setFieldValue) => {
        const isStreetValid = (typeof components.civico !== 'undefined') && (typeof components.via !== 'undefined');
        setFieldValue(indirizzoFatturazione.name, isStreetValid ? `${components.via}, ${components.civico}` : '');
        setFieldValue(comuneFatturazione.name, isStreetValid ? components.comune || "" : "");
        setFieldValue(capFatturazione.name, isStreetValid ? components.cap || "" : "");
      },
    },
    comuneFatturazione: {
      name: 'comuneFatturazione',
      label: <TranslatedText i18nKey="cases.request_info.fields.comune.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.comune.insert_placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'input',
      readonly: true
    },
    capFatturazione: {
      name: 'capFatturazione',
      label: <TranslatedText i18nKey="cases.request_info.fields.cap.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.cap.placeholder"/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.required'/>,
      type: 'input',
      readonly: true
    },
    pecFatturazione: {
      name: 'pecFatturazione',
      label: <TranslatedText i18nKey="cases.request_info.fields.pec.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.pec.placeholder"/>,
      invalidErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.pec.invalid'/>,
      type: 'input',
    },
    fileUpload: {
      name: 'fileUpload',
      label: <TranslatedText i18nKey="cases.request_info.fields.file.label"/>,
      invalidErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.file.not_supported'/>,
      requiredErrorMsg: <TranslatedText i18nKey='cases.request_info.errors.file.required'/>,
      type: 'file',
      allowedExtensions: ['application/pdf', 'image/jpeg']
    },
    consentMarketing: {
      name: 'consentMarketing',
      label: <TranslatedText i18nKey="form.privacy.label.first_checkbox"/>,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.required"/>,
      type: 'checkbox',
      optionalLabel: true
    },
    consentProfiling: {
      name: 'consentProfiling',
      label: <TranslatedText i18nKey="form.privacy.label.second_checkbox"/>,
      requiredErrorMsg: <TranslatedText i18nKey="cases.request_info.errors.required"/>,
      type: 'checkbox',
      optionalLabel: true
    },
    tipo: {
      name: 'tipo',
      label: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.placeholder"/>,
      requiredErrorMsg: <TranslatedText/>,
      type: 'input'
    },
    paymentType: {
      name: 'paymentType',
      label: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.label"/>,
      placeholder: <TranslatedText i18nKey="cases.request_info.fields.ragione_sociale.placeholder"/>,
      requiredErrorMsg: <TranslatedText/>,
      type: 'input'
    }

  }
};

const {
  formField: {
    businessName,
    integraClientCode,
    landlinePhoneNumber,
    mobilePhoneNumber,
    email,
    pec,
    nominativoAnalisi,
    comuneAnalisi,
    indirizzo,
    cap,
    nominativoFatturazione,
    codiceFiscale,
    partitaIvo,
    codiceFatturazione,
    comuneFatturazione,
    indirizzoFatturazione,
    capFatturazione,
    pecFatturazione,
    fileUpload,
    consentMarketing,
    consentProfiling,
    tipo,
    paymentType
  }
} = model;

// initialValues contiene i valori iniziali del form, ne imposta anche i campi.
// Si può usare per popolare il form eventualmente.
const initialValues = {
  [businessName.name]: '',
  [integraClientCode.name]: '',
  [landlinePhoneNumber.name]: '',
  [mobilePhoneNumber.name]: '',
  [email.name]: '',
  [pec.name]: '',
  [nominativoAnalisi.name]: '',
  [comuneAnalisi.name]: '',
  [indirizzo.name]: '',
  [cap.name]: '',
  [nominativoFatturazione.name]: '',
  [codiceFiscale.name]: '',
  [partitaIvo.name]: '',
  [codiceFatturazione.name]: '',
  [comuneFatturazione.name]: '',
  [comuneFatturazione.readonly]: true,
  [indirizzoFatturazione.name]: '',
  [capFatturazione.name]: '',
  [capFatturazione.readonly]: true,
  [pecFatturazione.name]: '',
  [fileUpload.name]: '',
  [consentMarketing.name]: false,
  [consentProfiling.name]: false,
  [tipo.name]: '',
  [paymentType.name]: ''
};

// Per definire il validationSchema si usa un array di shape che indicano gli step.
// L'ordine degli step deve corrispondere a quello in layout.
// You need a Yup shape for each step
const getValidationSchema = () => {
  const validationFiscalCode = getValidationRule('fiscalCode');
  const validationFiscalCodeOrVAT = getValidationRule('fiscalCodeOrVAT');
  const validPhone = getValidationRule('phone');
  const validEmail = getValidationRule('email');
  return [
    // step 1
    Yup.object().shape({
      [businessName.name]: Yup.string().required(businessName.requiredErrorMsg),
      [landlinePhoneNumber.name]: Yup.string().matches(validPhone, {
        message: landlinePhoneNumber.invalidErrorMsg
      }).required(landlinePhoneNumber.requiredErrorMsg),
      [mobilePhoneNumber.name]: Yup.string().matches(validPhone, {
        message: mobilePhoneNumber.invalidErrorMsg,
      }).required(mobilePhoneNumber.requiredErrorMsg),
      [email.name]: Yup.string().matches(validEmail, {
        message: email.invalidErrorMsg,
      }).required(email.requiredErrorMsg),
      [pec.name]: Yup.string().matches(validEmail, {
        message: pec.invalidErrorMsg,
      }),
    }),
    // step 2
    Yup.object().shape({
      [nominativoAnalisi.name]: Yup.string().required(nominativoAnalisi.requiredErrorMsg),
      [comuneAnalisi.name]: Yup.string().required(comuneAnalisi.requiredErrorMsg),
      [indirizzo.name]: Yup.string().required(indirizzo.requiredErrorMsg),
      [cap.name]: Yup.string().required(cap.requiredErrorMsg),
    }),
    // step 3
    Yup.object().shape({
      [nominativoFatturazione.name]: Yup.string().required(nominativoFatturazione.requiredErrorMsg),
      [codiceFiscale.name]: Yup.string()
        .min(11, <TranslatedText i18nKey="errors.registration.public.fc.short"/>)
        .max(16, <TranslatedText i18nKey="errors.registration.public.fc.long"/>)
        .matches(validationFiscalCode, {message: codiceFiscale.invalidErrorMsg})
        .when(partitaIvo.name, {
          is: (partitaIvo) => !partitaIvo || partitaIvo.length === 0,
          then: Yup.string()
            .required(partitaIvo.requiredErrorMsg),
        }),
      [partitaIvo.name]: Yup.string()
        .min(11, <TranslatedText i18nKey="errors.registration.public.fc.short"/>)
        .max(16, <TranslatedText i18nKey="errors.registration.public.fc.long"/>)
        .matches(validationFiscalCodeOrVAT, {message: partitaIvo.invalidErrorMsg})
        .when(codiceFiscale.name, {
          is: (codiceFiscale) => !codiceFiscale || codiceFiscale.length === 0,
          then: Yup.string().required(partitaIvo.requiredErrorMsg),
        })
      // [codiceFatturazione.name]: Yup.string().required(codiceFatturazione.requiredErrorMsg),
    }, [codiceFiscale.name, partitaIvo.name]),
    // step 4
    Yup.object().shape({
      [comuneFatturazione.name]: Yup.string().required(comuneFatturazione.requiredErrorMsg),
      [indirizzoFatturazione.name]: Yup.string().required(indirizzoFatturazione.requiredErrorMsg),
      [capFatturazione.name]: Yup.string().required(capFatturazione.requiredErrorMsg),
      [pecFatturazione.name]: Yup.string().matches(validEmail, {
        message: pecFatturazione.invalidErrorMsg,
      }),
    }),
    // step 5
    Yup.object().shape({}),
    // step 6
    Yup.object().shape({
      [fileUpload.name]: Yup.mixed().when(paymentType.name, {
        is: field => field === PaymentType.BANK,
        then: Yup.mixed()
          .required(fileUpload.requiredErrorMsg)
      })
      // .test('fileFormat', fileUpload.invalidErrorMsg, (file) => {
      //   return file && fileUpload.allowedExtensions.includes(file.type)
      // }),
    }),
  ]
}

// Il layout contiene informazioni di testo e di distribuzione sulle griglie
const getLayout = (privacyLink, privacyCtaLabel) => {
  return [
    // step 1
    {
      chapter: true,
      chapterName: 'Dati del richiendente',
      title: <TranslatedText i18nKey='cases.analisi_acqua.titles.title1'/>,
      beforeSubmit: async (values, {setFieldValue, setErrors}) => {
        await new Promise((resolve, reject) => {
          if (values.email === "") {
            reject();
          } else {
            window.dataLayer.push({"event": "form1_dati_richiedente"})
            resolve();
          }
        });
      },
      fields: [
        {
          fieldName: 'businessName',
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: 'integraClientCode',
          grid: {
            xs: 12,
            md: 6,
          }
        },
        {
          fieldName: 'landlinePhoneNumber',
          grid: {
            xs: 12,
            md: 6
          }
        },
        {
          fieldName: 'mobilePhoneNumber',
          grid: {
            xs: 12,
            md: 6
          }
        },
        {
          fieldName: 'email',
          grid: {
            xs: 12,
            md: 6
          }
        },
        {
          fieldName: 'pec',
          grid: {
            xs: 12,
            md: 6
          }
        },
      ],
    },
    // step 2
    {
      chapter: true,
      chapterName: 'Indirizzo analisi',
      title: <TranslatedText i18nKey='cases.analisi_acqua.titles.title2'/>,
      beforeSubmit: async (values, {setFieldValue, setErrors}) => {
        await new Promise((resolve, reject) => {
          console.log(values)
          if (values.businessName === "") {
            reject();
          } else {
            window.dataLayer.push({"event": "form2_indirizzo_analisi"})
            resolve();
          }
        });
      },
      fields: [
        {
          fieldName: 'nominativoAnalisi',
          grid: {
            xs: 12,
          },
        },
        {
          fieldName: 'comuneAnalisi',
          grid: {
            xs: 12,
            md: 6,
          }
        },
        {
          fieldName: 'indirizzo',
          grid: {
            xs: 12,
            md: 6
          }
        },
        {
          fieldName: 'cap',
          grid: {
            xs: 12,
            md: 6
          }
        },
      ]
    },
    // step 3
    {
      chapter: true,
      chapterName: 'Dati di fatturazione',
      title: <TranslatedText i18nKey="cases.analisi_acqua.titles.title3"/>,
      beforeSubmit: async (values, {setFieldValue, setErrors}) => {
        await new Promise((resolve, reject) => {
          if (values.codiceFiscale === "" && values.partitaIvo === "") {
            setErrors({codiceFiscale: codiceFiscale.requiredErrorMsg});
            reject();
          } else {
            window.dataLayer.push({"event": "form3_dati_fatturazione"})
            resolve();
          }
        });
      },
      fields: [
        {
          fieldName: "nominativoFatturazione",
          grid: {
            xs: 12,
          },
        },
        {
          fieldName: "codiceFiscale",
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: "partitaIvo",
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: "codiceFatturazione",
          grid: {
            xs: 12,
          },
        },
      ],
    },
    // step 4
    {
      chapter: true,
      chapterName: 'Indirizzo di fatturazione',
      title: <TranslatedText i18nKey="cases.analisi_acqua.titles.title4"/>,
      beforeSubmit: async (values, {setFieldValue, setErrors}) => {
        await new Promise((resolve, reject) => {
          console.log(values)
          if (values.businessName === "") {
            reject();
          } else {
            window.dataLayer.push({"event": "form4_indirizzo_fatturazione"})
            resolve();
          }
        });
      },
      fields: [{
        fieldName: "indirizzoFatturazione",
        grid: {
          xs: 12,
        },
      }, {
        fieldName: "comuneFatturazione",
        grid: {
          xs: 12,
          md: 6,
        },
      }, {
        fieldName: "capFatturazione",
        grid: {
          xs: 12,
          md: 6,
        },
      }, {
        fieldName: "pecFatturazione",
        grid: {
          xs: 12,
        },
      },
      ]
    },
    // step 5 privacy consent
    {
      chapter: true,
      chapterName: 'Consensi',
      noMandatoryLegend: true,
      title: <TranslatedText i18nKey='form.privacy.label.title_third_scenario'/>,
      beforeSubmit: async (values, {setFieldValue, setErrors}) => {
        await new Promise((resolve, reject) => {
          console.log(values)
          if (values.businessName === "") {
            reject();
          } else {
            window.dataLayer.push({"event": "form5_consensi"})
            resolve();
          }
        });
      },
      fields: [
        {
          fieldName: null,
          text: (
            <div className="text-header">
              <div>
                <PrivacyPolicy privacyLink={privacyLink} privacyCtaLabel={privacyCtaLabel}/>
              </div>
            </div>
          ),
          grid: {
            xs: 12,
          }
        },
        {
          fieldName: 'consentMarketing',
          grid: {
            xs: 12
          },
        },
        {
          fieldName: 'consentProfiling',
          grid: {
            xs: 12
          },
        },
      ],
    },
    // step 6 upload payment
    {
      chapter: true,
      chapterName: 'Allega ricevuto bonifico',
      title: <TranslatedText i18nKey="cases.analisi_acqua.titles.title5"/>,
      beforeSubmit: async (values, {setFieldValue, setErrors}) => {
        await new Promise((resolve, reject) => {
          const {paymentType} = values;
          if (values.businessName === "") {
            reject();
          } else {
            if (paymentType === "BANK_TRANSFER") {
              console.log(values, 'ONLY FOR BANK TRANSFER');
              window.dataLayer.push({"event": "form6_allega_bonifico"})
            }
            resolve();
          }
        });
      },
      alternativeTitle: <TranslatedText i18nKey="cases.analisi_acqua.titles.title5_alternative"/>,
      alternativeCondition: [{
        if: 'paymentType',
        is: PaymentType.CREDIT_CARD
      }],
      fields: [{
        fieldName: "fileUpload",
        grid: {
          xs: 12,
        },
        condition: [{
          if: 'paymentType',
          is: PaymentType.BANK
        }]
      },
        {
          fieldName: null,
          text: <div className="text-header">
            <p>
              {<TranslatedText i18nKey='cases.analisi_acqua.payment.credit_card.part1'/>}
            </p>
            <p>
              {<TranslatedText i18nKey='cases.analisi_acqua.payment.credit_card.part2'/>}
            </p>
            <p>
              {<TranslatedText i18nKey='cases.analisi_acqua.payment.credit_card.part3'/>}
            </p>
          </div>,
          grid: {
            xs: 12,
          },
          condition: [{
            if: 'paymentType',
            is: PaymentType.CREDIT_CARD
          }]
        },
      ],
    },
  ]
}

// Contiene dettagli sul case form
const formDetails = {
  summary: false,
  stepper: CaseStepperType.Simple,
  helpText: null,
  successText: (
    <div>
      <p><TranslatedText i18nKey="cases.request_info.success_text"/></p>
    </div>
  )
}
const translatedMapping = (payload) => {
  return {
    agreement: {
      privacy: true,
      profiling: Boolean(payload[consentProfiling.name]),
      marketing: Boolean(payload[consentMarketing.name]),
    },
    attachments: [{
      type: AttachmentType.SIMPLE_CASE,
      data: payload[fileUpload.name]
    }],
    businessName: payload[businessName.name],
    category: CaseForms.AnalisiAcqua,
    integraClientCode: payload[integraClientCode.name],
    landlinePhoneNumber: payload[landlinePhoneNumber.name],
    mobilePhoneNumber: payload[mobilePhoneNumber.name],
    email: payload[email.name],
    pec: payload[pec.name],
    billing: {
      name: payload[nominativoFatturazione.name],
      fiscalCode: payload[codiceFiscale.name],
      vatNumber: payload[partitaIvo.name],
      city: payload[comuneFatturazione.name],
      street: payload[indirizzoFatturazione.name],
      postalCode: payload[capFatturazione.name],
      pec: payload[pecFatturazione.name],
      billingCode: payload[codiceFatturazione.name],
    },
    pufAddress: {
      name: payload[nominativoAnalisi.name],
      city: payload[comuneAnalisi.name],
      street: payload[indirizzo.name],
      postalCode: payload[cap.name],
    },
    type: AnalisiAcquaType[payload[tipo.name]],
    payment: {
      amount: AnalisiAcquaPrices[payload[tipo.name]].toString(),
      type: payload[paymentType.name]
    }
  }
}
const translatedInverseMapping = (payload) => {
  const errorsFields = payload.map(({field, message}) => {
    return {field, message}
  });
  const mapping = {
    "req.agreement.profiling": consentProfiling.name,
    "req.agreement.marketing": consentMarketing.name,
    "req.attachments.data": fileUpload.name,
    "req.billing.name": nominativoFatturazione.name,
    "req.billing.fiscalCode": codiceFiscale.name,
    "req.billing.vatNumber": partitaIvo.name,
    "req.billing.city": comuneFatturazione.name,
    "req.billing.street": indirizzoFatturazione.name,
    "req.billing.postalCode": capFatturazione.name,
    "req.billing.pec": pecFatturazione.name,
    "req.billing.billingCode": codiceFatturazione.name,
    "req.businessName": businessName.name,
    "req.email": email.name,
    "req.integraClientCode": integraClientCode.name,
    "req.landlinePhoneNumber": landlinePhoneNumber.name,
    "req.mobilePhoneNumber": mobilePhoneNumber.name,
    "req.pec": pec.name,
    "req.pufAddress.name": nominativoAnalisi.name,
    "req.pufAddress.city": comuneAnalisi.name,
    "req.pufAddress.street": indirizzo.name,
    "req.pufAddress.postalCode": cap.name,
  }


  return errorsFields.reduce((acc, item) => {
    acc[mapping[item.field]] = item.message;
    return acc;
  }, {});
}
const mutation = {
  type: CaseMutationType.Custom,
  mutation: SEND_SIMPLE_CASE,
  refetchQueries: getEvictionForMutation("simpleCase"),
}

const main = (excludeSteps, counter, signDigital, webviewPopup, webviewRedirectPopup, buttonMatricolaContatoreCase, privacyLink, privacyCtaLabel) => {
  return {
    model,
    validationSchema: getValidationSchema(),
    initialValues,
    translatedMapping,
    translatedInverseMapping,
    layout: getLayout(privacyLink, privacyCtaLabel),
    formDetails,
    mutation
  };
}

export default main;
